import { Dialog } from '../../Dialog';
import styles from './styles';
import React, {FC, useEffect, useState} from 'react';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  OutlinedInput,
  Select,
  Switch,
  Typography
} from '@mui/material';
import {Add} from '@mui/icons-material';
import {CixonTables} from './constants';
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

interface ICellConfigField {
  table?: string;
  field?: string;
  text?: string;
  html?: string;
  type: 'table' | 'text' | 'html';
  valueType?: 'number' | 'date' | 'string';
  isin?: string;
  defaultValue?: string;
  relatedValue?: string;
}

interface ICellConfig {
  isLinkWithPdfField?: boolean;
  isDynamic: boolean;
  fields: ICellConfigField[];
  pdfField?: { table?: string, cell?: string };
}

interface ICellConfigProps {
  isin: string;
  config: ICellConfig | undefined;
  open: boolean;
  isTitle?: boolean | undefined;
  onClose: () => void;
  onConfirm: (data: any) => void;
}

export const CellConfigDialog: FC<ICellConfigProps> = ({
  isin,
  config,
  open,
  isTitle,
  onClose,
  onConfirm,
}) => {
  const [cellConfig, setCellConfig] = useState<ICellConfig>({
    isDynamic: true,
    fields: [],
  });
  const [selectedFieldIndex, setSelectedFieldIndex] = useState<number | null>(null);

  useEffect(() => {
    if (typeof config === 'object' && config.fields) {
      setCellConfig(config);
    }
  }, []);

  const handleSwitchDynamic = (e) => {
    setCellConfig({
      ...cellConfig,
      isDynamic: e.target.checked,
    });
  };

  const handleLinkWithPdfField = (e) => {
    setCellConfig({
      ...cellConfig,
      isLinkWithPdfField: e.target.checked,
    });
  };

  const handleChangePdfField = (key: string, value) => {
    setCellConfig({
      ...cellConfig,
      pdfField: { ...(cellConfig.pdfField || {}), [key]: value },
    })
  };

  const handleConfirm = () => {
    onConfirm(cellConfig);
  };

  const handleAddField = () => {
    setSelectedFieldIndex(cellConfig.fields.length);
    setCellConfig({
      ...cellConfig,
      fields: [...cellConfig.fields, { type: 'table', table: '', field: '', valueType: 'string' }],
    });
  };

  const handleChangeFieldConfig = (e: any) => {
    setCellConfig({
      ...cellConfig,
      fields: cellConfig.fields.map((field, index) => index === selectedFieldIndex ? {
        ...field,
        [e.target.name]: e.target.value,
      } : field),
    })
  };

  const handleSelectField = (index: number | null) => {
    setSelectedFieldIndex(index);
  };

  const handleDeleteField = () => {
    setCellConfig({
      ...cellConfig,
      fields: cellConfig.fields.filter((_, index) => index !== selectedFieldIndex),
    })
    setSelectedFieldIndex(null);
  };

  const onDragComplete = (result) => {
    if (!result.destination) return;

    const arr = [...cellConfig.fields];

    //Changing the position of Array element
    const removedItem = arr.splice(result.source.index, 1)[0];
    arr.splice(result.destination.index, 0, removedItem);

    //Updating the list
    setCellConfig({
      ...cellConfig,
      fields: arr,
    });
  };
  return (
    <Dialog open={open} onClose={onClose} sx={styles.dialog}>
      <Box sx={{ width: '100%' }}>
        {!isTitle && (
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              label="Is Dynamic?"
              sx={styles.switch}
              control={
                <Switch
                  checked={cellConfig.isDynamic}
                  color="secondary"
                  onChange={handleSwitchDynamic}
                />
              }
            />

            <Box sx={{ display: 'flex', grid: '8px' }}>
              <FormControlLabel
                label="Is linked with PDF field?"
                sx={styles.switch}
                control={
                  <Switch
                    checked={cellConfig.isLinkWithPdfField}
                    color="secondary"
                    onChange={handleLinkWithPdfField}
                  />
                }
              />

              {cellConfig.isLinkWithPdfField && (
                <Box sx={{ display: 'flex', grid: '4px' }}>
                  <Select sx={{ width: '150px' }} value={cellConfig?.pdfField?.table} onChange={(e) => handleChangePdfField('table', e.target.value)}>
                    <MenuItem value="preise">Preis</MenuItem>
                    <MenuItem value="konditionen">Konditionen</MenuItem>
                  </Select>
                  <Select sx={{ ml: '8px', width: '150px' }} value={cellConfig?.pdfField?.cell} onChange={(e) => handleChangePdfField('cell', e.target.value)}>
                    <MenuItem value="ausschuttung">ausschuttung</MenuItem>
                    <MenuItem value="fondsvolumen">fondsvolumen</MenuItem>
                    <MenuItem value="kursper">kursper</MenuItem>
                    <MenuItem value="letzte">letzte</MenuItem>
                    <MenuItem value="mepreis">mepreis</MenuItem>
                    <MenuItem value="wahrung">wahrung</MenuItem>
                  </Select>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', gap: 8, mt: 16 }}>
          <DragDropContext onDragEnd={onDragComplete}>
            <Droppable droppableId="drag-drop-list" direction="horizontal">
              {(provided, snapshot) => (
                <Box
                  className="drag-drop-list-container"
                  sx={styles.configWrapper}
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {cellConfig?.fields.map((field, index) => (
                    <Draggable key={index} draggableId={`${index}_${field.text}`} index={index}>
                      {(provided) => (
                        <Box
                          className="item-card"
                          ref={provided.innerRef}
                          sx={(theme) => styles.configField(
                            theme,
                            { type: field.type, active: selectedFieldIndex === index }
                          )}
                          onClick={() => handleSelectField(index)}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                        >
                          {field.type === 'html' ? <div dangerouslySetInnerHTML={{ __html: field.html || '' }} /> : (
                          <Typography variant="subtitle2">
                            {field.type === 'text'
                              ? field.text
                              : `${field.table || '(table)'}.${field.field || '(field)'}`
                            }
                          </Typography>)}
                        </Box>
                      )}
                    </Draggable>
                  ))}
                </Box>
              )}
            </Droppable>
          </DragDropContext>
          <IconButton sx={{ width: 48, height: 48 }} onClick={handleAddField}><Add /></IconButton>
        </Box>

        {selectedFieldIndex !== null && (
          <Box sx={styles.fieldOption}>
            <Typography variant="subtitle1">Field {selectedFieldIndex + 1}:</Typography>
            <Box sx={styles.fieldOptionForm}>
              <Typography variant="subtitle2">Type:</Typography>
              <Select
                name="type"
                value={cellConfig.fields[selectedFieldIndex]?.type}
                onChange={handleChangeFieldConfig}
              >
                <MenuItem value="table">Table</MenuItem>
                <MenuItem value="text">Text</MenuItem>
                <MenuItem value="html">Html</MenuItem>
              </Select>
            </Box>
            {cellConfig.fields[selectedFieldIndex]?.type === 'table' && (
              <>
                <Box sx={styles.fieldOptionForm}>
                  <Typography variant="subtitle2">Table:</Typography>
                  <Select
                    name="table"
                    value={cellConfig.fields[selectedFieldIndex]?.table}
                    onChange={handleChangeFieldConfig}
                  >
                    {CixonTables.map((table) => (
                      <MenuItem value={table.value}>{table.name}</MenuItem>
                    ))}
                  </Select>
                </Box>
                <Box sx={styles.fieldOptionForm}>
                  <Typography variant="subtitle2">Column:</Typography>
                  <Select
                    name="field"
                    value={cellConfig.fields[selectedFieldIndex]?.field}
                    onChange={handleChangeFieldConfig}
                  >
                    {CixonTables
                      .find((table) => table.value === cellConfig.fields[selectedFieldIndex]?.table)?.fields
                      .map((field) => (
                        <MenuItem value={field}>{field}</MenuItem>
                      ))
                    }
                  </Select>
                </Box>
                <Box sx={styles.fieldOptionForm}>
                  <Typography variant="subtitle2">Value Type:</Typography>
                  <Select
                    name="valueType"
                    label="Value Type"
                    value={cellConfig.fields[selectedFieldIndex]?.valueType || 'string'}
                    onChange={handleChangeFieldConfig}
                  >
                    <MenuItem value="string">String</MenuItem>
                    <MenuItem value="number">Number</MenuItem>
                    <MenuItem value="percentage">Percentage</MenuItem>
                    <MenuItem value="date">Date</MenuItem>
                  </Select>
                </Box>
                <Box sx={styles.fieldOptionForm}>
                  <Typography variant="subtitle2">ISIN:</Typography>
                  <OutlinedInput
                    name="isin"
                    value={cellConfig.fields[selectedFieldIndex]?.isin}
                    defaultValue={isin}
                    onChange={handleChangeFieldConfig}
                  />
                </Box>
                <Box sx={styles.fieldOptionForm}>
                  <Typography variant="subtitle2">Default Value:</Typography>
                  <OutlinedInput
                    name="defaultValue"
                    value={cellConfig.fields[selectedFieldIndex]?.defaultValue}
                    onChange={handleChangeFieldConfig}
                  />
                </Box>
              </>
            )}
            {cellConfig.fields[selectedFieldIndex]?.type === 'text' && (
              <>
                <Box sx={styles.fieldOptionForm}>
                  <Typography variant="subtitle2">Text:</Typography>
                  <OutlinedInput
                    name="text"
                    value={cellConfig.fields[selectedFieldIndex]?.text}
                    onChange={handleChangeFieldConfig}
                  />
                </Box>
                <Box sx={styles.fieldOptionForm}>
                  <Typography variant="subtitle2">Related Value:</Typography>
                  <Select
                    name="relatedValue"
                    value={cellConfig.fields[selectedFieldIndex]?.relatedValue || ''}
                    onChange={handleChangeFieldConfig}
                    displayEmpty
                  >
                    <MenuItem value="">
                      <em>None</em>
                    </MenuItem>
                    {cellConfig.fields
                      .map((field, index) => {
                        if (index === selectedFieldIndex) return null;
                        const displayText = field.type === 'text'
                          ? field.text || `Field ${index + 1}`
                          : field.type === 'html'
                          ? `Field ${index + 1} (HTML)`
                          : `${field.table || '(table)'}.${field.field || '(field)'}`;
                        return (
                          <MenuItem key={index} value={index.toString()}>
                            {displayText}
                          </MenuItem>
                        );
                      })
                      .filter(Boolean)}
                  </Select>
                </Box>
              </>
            )}
            {cellConfig.fields[selectedFieldIndex]?.type === 'html' && (
              <Box sx={styles.fieldOptionForm}>
                <Typography variant="subtitle2">Html:</Typography>
                <OutlinedInput
                  sx={{ height: '120px', width: '380px' }}
                  name="html"
                  multiline
                  rows={4}
                  value={cellConfig.fields[selectedFieldIndex]?.html}
                  onChange={handleChangeFieldConfig}
                />
              </Box>
            )}
            <Box display="flex" alignItems="center" gap="8px" ml="270px">
              <Button
                onClick={handleDeleteField}
              >
                Delete
              </Button>
              <Button
                onClick={() => handleSelectField(null)}
              >
                Okay
              </Button>
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', mt: '16px' }}>
          <Button variant="outlined" onClick={handleConfirm}>
            Cancel
          </Button>
          <Button onClick={handleConfirm}>
            Save
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};
